import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import SidebarPanel from '../admin/SidebarPanel';

// ============================================================================
// Constants
// ============================================================================

const PRIORITY = {
  LOW: 0,
  NORMAL: 1,
  HIGH: 2,
  URGENT: 3
};

const PRIORITY_CONFIG = {
  [PRIORITY.LOW]: { color: 'secondary', label: 'Low', icon: 'minus' },
  [PRIORITY.NORMAL]: { color: 'info', label: 'Normal', icon: 'minus' },
  [PRIORITY.HIGH]: { color: 'warning', label: 'High', icon: 'arrow-up' },
  [PRIORITY.URGENT]: { color: 'danger', label: 'Urgent', icon: 'fire' }
};

const STATUS_CONFIG = {
  pending: { color: 'secondary', borderColor: 'border-secondary' },
  scheduled: { color: 'info', borderColor: 'border-info' },
  in_progress: { color: 'primary', borderColor: 'border-primary' },
  completed: { color: 'success', borderColor: 'border-success' },
  failed: { color: 'danger', borderColor: 'border-danger' },
  cancelled: { color: 'dark', borderColor: 'border-dark' }
};

const FILTER_OPTIONS = ['pending', 'in_progress', 'completed', 'failed', 'cancelled', 'all'];
const REFRESH_INTERVAL = 5000; // 5 seconds

// ============================================================================
// Main Component
// ============================================================================

/**
 * CallQueuePanel - Side panel for managing Retell AI call queue
 * Prevents carrier-level 477 SIP errors by spacing calls with random intervals
 */
const CallQueuePanel = ({ isOpen, onClose }) => {
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [draggedItem, setDraggedItem] = useState(null);

  // Fetch queue data
  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const [queueRes, statsRes] = await Promise.all([
        adminApi.getCallQueue({ status: filter }),
        adminApi.getCallQueueStats()
      ]);
      if (queueRes.data.success) setCalls(queueRes.data.calls);
      if (statsRes.data.success) setStats(statsRes.data.stats);
    } catch (err) {
      console.error('[CallQueue] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isOpen) {
      fetchQueue();
      const interval = setInterval(fetchQueue, REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchQueue]);

  // Event handlers
  const handleCancel = async (callId) => {
    if (!window.confirm('Cancel this call?')) return;
    try {
      await adminApi.cancelCallQueueItem(callId, 'Cancelled by user');
      fetchQueue();
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePriority = async (callId, newPriority) => {
    try {
      await adminApi.updateCallQueueItem(callId, { priority: newPriority });
      fetchQueue();
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDragStart = (e, call) => {
    setDraggedItem(call);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetCall) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetCall.id) return;

    const reorderedCalls = [...calls];
    const dragIdx = reorderedCalls.findIndex(c => c.id === draggedItem.id);
    const targetIdx = reorderedCalls.findIndex(c => c.id === targetCall.id);

    reorderedCalls.splice(dragIdx, 1);
    reorderedCalls.splice(targetIdx, 0, draggedItem);

    const updates = reorderedCalls.map((call, idx) => ({
      id: call.id,
      queue_position: idx + 1
    }));

    try {
      await adminApi.reorderCallQueue(updates);
      fetchQueue();
    } catch (err) {
      alert(`Reorder error: ${err.response?.data?.error || err.message}`);
    }
    setDraggedItem(null);
  };

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title={<>Call Queue</>}
    >
      <FilterBar filter={filter} setFilter={setFilter} />
      {stats && (
        <div className="card card-body bg-light mb-2">
          <StatsSection stats={stats} />
        </div>
      )}
      <div className="card card-body mb-2">
        <CallList
          loading={loading}
          calls={calls}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onPriority={handlePriority}
          onCancel={handleCancel}
        />
      </div>
      <div className="card card-body bg-light">
        <Footer />
      </div>
    </SidebarPanel>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

const StatsSection = ({ stats }) => (
    <div className="row g-2">
      <StatCard label="Pending" value={stats.pending_count} color="warning" />
      <StatCard label="In Progress" value={stats.in_progress_count} color="primary" />
      <StatCard label="Initiated" value={stats.completed_today} color="success" />
      <StatCard label="Failed" value={stats.failed_today} color="danger" />
      {stats.pending_count > 0 && (
        <div className="col-12">
          <div className="card border-0 text-center bg-primary text-white">
            <div className="card-body p-2">
              <small>Est. Wait Time</small>
              <h5 className="text-white mb-0">{Math.ceil(stats.estimated_wait_seconds / 60)} minutes</h5>
            </div>
          </div>
        </div>
      )}
    </div>
);

const StatCard = ({ label, value, color }) => (
  <div className="col-sm-6 col-md-3">
    <div className="card border-0 text-center">
      <div className="card-body p-2">
        <small className="text-muted d-block">{label}</small>
        <h4 className={`mb-0 text-${color}`}>{value}</h4>
      </div>
    </div>
  </div>
);

const FilterBar = ({ filter, setFilter }) => (
  <ul className="nav nav-tabs mb-3">
    {FILTER_OPTIONS.map(f => (
      <li key={f} className="nav-item">
        <button
          className={`nav-link ${filter === (f === 'all' ? '' : f) ? 'active' : ''}`}
          onClick={() => setFilter(f === 'all' ? '' : f)}
          style={{ textTransform: 'capitalize' }}
        >
          {f.replace('_', ' ')}
        </button>
      </li>
    ))}
  </ul>
);

const CallList = ({ loading, calls, onDragStart, onDragOver, onDrop, onPriority, onCancel }) => {
  if (loading) {
    return (
      <div className="flex-grow-1 overflow-auto p-3">
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
        </div>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="flex-grow-1 overflow-auto p-3">
        <div className="text-center text-muted py-5">
          <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
          <p>No calls in queue</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {calls.map((call) => (
        <CallCard
          key={call.id}
          call={call}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onPriority={onPriority}
          onCancel={onCancel}
        />
      ))}
    </>
  );
};

const CallCard = ({ call, onDragStart, onDragOver, onDrop, onPriority, onCancel }) => {
  const isDraggable = call.status === 'pending' || call.status === 'scheduled';
  const statusConfig = STATUS_CONFIG[call.status] || STATUS_CONFIG.pending;

  return (
    <div
      className={`mb-3 border-start ${statusConfig.borderColor}`}
      draggable={isDraggable}
      onDragStart={(e) => onDragStart(e, call)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, call)}
      style={{ cursor: isDraggable ? 'grab' : 'default' }}
    >
      <div className="card-body p-3">
        <CallCardHeader call={call} />
        <CallCardMeta call={call} />
        {(call.retell_response?.message || call.error_message) && (
          <CallCardError
            message={call.retell_response?.message || call.error_message}
            callStatus={call.retell_response?.call_status}
          />
        )}
        {isDraggable && (
          <CallCardActions
            callId={call.id}
            onPriority={onPriority}
            onCancel={onCancel}
          />
        )}
      </div>
    </div>
  );
};

const CallCardHeader = ({ call }) => (
  <div className="d-flex justify-content-between align-items-center mb-2">
    <div>
      <i className={`fas fa-${call.direction === 'outbound' ? 'arrow-right' : 'arrow-left'} me-2 text-muted`}></i>
      <strong>{call.to_number}</strong>
    </div>
    <div>
      <PriorityBadge priority={call.priority} />
      <StatusBadge status={call.status} />
    </div>
  </div>
);

const CallCardMeta = ({ call }) => {
  const formatTime = (isoStr) => {
    if (!isoStr) return 'N/A';
    return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="row g-2 small text-muted mb-2">
      {call.dynamic_variables?.prospect_name && (
        <div className="col-6">
          <i className="fas fa-user me-1"></i>{call.dynamic_variables.prospect_name}
        </div>
      )}
      {call.dynamic_variables?.company_name && (
        <div className="col-6">
          <i className="fas fa-building me-1"></i>{call.dynamic_variables.company_name}
        </div>
      )}
      {call.scheduled_at && (
        <div className="col-6">
          <i className="far fa-clock me-1"></i>ETA: {formatTime(call.scheduled_at)}
        </div>
      )}
      <div className="col-6">
        <i className="fas fa-list-ol me-1"></i>Position: {call.queue_position}
      </div>
      {call.attempts > 0 && (
        <div className="col-6">
          <i className="fas fa-redo me-1"></i>Attempt {call.attempts}/{call.max_attempts}
        </div>
      )}
    </div>
  );
};

const CallCardError = ({ message, callStatus }) => (
  <div className="alert alert-danger py-2 small mb-2">
    <i className="fas fa-exclamation-circle me-2"></i>
    <strong>{callStatus === 'not_connected' ? 'Call Not Connected:' : callStatus === 'error' ? 'Call Error:' : 'Failed:'}</strong>
    <div className="mt-1">{message}</div>
  </div>
);

const CallCardActions = ({ callId, onPriority, onCancel }) => (
  <div className="d-flex justify-content-between pt-2">
    <div>
      <button
      className="btn btn-sm btn-outline-danger me-2"
      onClick={() => onPriority(callId, PRIORITY.URGENT)}
      title="Urgent"
    >
      <i className="fas fa-fire"></i>
    </button>
    <button
      className="btn btn-sm btn-outline-warning me-2"
      onClick={() => onPriority(callId, PRIORITY.HIGH)}
      title="High"
    >
      <i className="fas fa-arrow-up"></i>
    </button>
    <button
      className="btn btn-sm btn-outline-secondary"
      onClick={() => onPriority(callId, PRIORITY.NORMAL)}
      title="Normal"
    >
      <i className="fas fa-minus"></i>
    </button>
    </div>
    <button
      className="btn btn-sm btn-danger"
      onClick={() => onCancel(callId)}
      title="Cancel"
    >
      <i className="fas fa-times"></i>
    </button>
  </div>
);

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[PRIORITY.NORMAL];
  return <span className={`badge bg-${config.color} me-1`}>{config.label}</span>;
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <span className={`badge bg-${config.color}`}>{status}</span>;
};

const Footer = () => (
    <small className="text-muted">
      <i className="fas fa-info-circle me-2"></i>
      Calls spaced randomly (10-30s) to avoid carrier blocking
    </small>
);

export default CallQueuePanel;
